import { guestApi } from '../../api/guest';
import {
  sedrApi,
  type SedrFile,
  type SedrPhase,
  type SedrRequirement,
} from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { enhanceDataTableCards } from '../../ui/dataTableCards';
import { openFilesContextMenu, type FilesContextMenuItem } from '../../ui/filesContextMenu';
import { iconImg } from '../../ui/icons';
import { filterRequirementsByPhase, type CompliancePhaseId } from './sedrDashboard';
import { showSedrCreateRequirementDialog } from './sedrCreateRequirementDialog';
import { showSedrExportDialog } from './sedrExportDialog';
import {
  applyRequirementFilters,
  collectRequirementOwners,
  detectSedrStatusQuickLink,
  getCachedSedrCategories,
  loadSedrCategories,
  readComplianceSort,
  readFiltersCollapsed,
  renderComplianceFilterBar,
  searchRequirements,
  sedrAllActiveStatusFilter,
  sedrAllStatusFilter,
  SEDR_LIFECYCLE_STATUSES,
  selectedObsoleteStatuses,
  sortRequirements,
  statusBadgeClass,
  statusFilterHidesObsolete,
  setObsoleteSubtypesInStatusFilter,
  displaySedrCategory,
  obsoleteFilterChipLabel,
  tSedrStatus,
  writeComplianceSort,
  writeFiltersCollapsed,
  type SedrCategoryFilter,
  type SedrComplianceSort,
  type SedrComplianceSortKey,
  type SedrEvidenceFilter,
  type SedrLifecycleRequestFilter,
  type SedrOwnerFilter,
  type SedrRenewalFilter,
  type SedrStatusFilter,
} from './sedrUi';
import { renderSedrBulkRequirementPanel } from './sedrBulkRequirementPanel';
import { formatSedrRequirementLinks, formatSedrRequirementLinksTooltip } from './sedrExport';
import { renderSedrRequirementPanel } from './sedrRequirementPanel';
import { isSedrMobileLayout, SEDR_MOBILE_LAYOUT_MQ } from './sedrRoomChrome';
import type { SedrFilePickerUploadResult } from './sedrFilePickerDialog';
import type { BlobUploadProgress } from '../../api/blobUpload';

const SEARCH_DEBOUNCE_MS = 150;
const COMMENT_SEARCH_DEBOUNCE_MS = 300;
const EMPTY_ROW_CLASS = 'sedr-compliance-empty-row';
const SPLIT_STORAGE_KEY = 'shareweb:sedr-compliance-split';
/** Matches historical CSS `1fr` / `1.15fr` bias. */
const DEFAULT_LIST_FRACTION = 1 / (1 + 1.15);
const MIN_LIST_REM = 18;
const MIN_DETAIL_REM = 22;
const SPLIT_NARROW_MQ = '(max-width: 960px)';

type SedrComplianceMobileStep = 'filters' | 'list' | 'detail';

const MOBILE_STEP_CLASSES: Record<SedrComplianceMobileStep, string> = {
  filters: 'sedr-tab-panel--compliance-step-filters',
  list: 'sedr-tab-panel--compliance-step-list',
  detail: 'sedr-tab-panel--compliance-step-detail',
};

function remToPx(rem: number): number {
  const root = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
  return rem * (Number.isFinite(root) && root > 0 ? root : 16);
}

function readStoredListFraction(): number {
  try {
    const raw = localStorage.getItem(SPLIT_STORAGE_KEY);
    if (raw == null) return DEFAULT_LIST_FRACTION;
    const n = Number(raw);
    if (!Number.isFinite(n)) return DEFAULT_LIST_FRACTION;
    return n;
  } catch {
    return DEFAULT_LIST_FRACTION;
  }
}

function writeStoredListFraction(fraction: number): void {
  try {
    localStorage.setItem(SPLIT_STORAGE_KEY, String(fraction));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

function clampListFraction(
  fraction: number,
  availablePx: number,
  minListPx: number,
  minDetailPx: number,
): number {
  if (!(availablePx > 0) || availablePx <= minListPx + minDetailPx) {
    // Keep the preferred fraction until we have a real layout width.
    return fraction;
  }
  const minFrac = minListPx / availablePx;
  const maxFrac = 1 - minDetailPx / availablePx;
  return Math.min(Math.max(fraction, minFrac), maxFrac);
}

function applyComplianceSplitColumns(
  split: HTMLElement,
  listFraction: number,
  availablePx: number,
  gutterPx: number,
  minListPx: number,
  minDetailPx: number,
): void {
  // Use an explicit list pixel width so the stored fraction matches what the user dragged.
  // (fr + minmax would redistribute after mins and look like persistence failed.)
  const listPx = Math.round(
    clampListFraction(listFraction, availablePx, minListPx, minDetailPx) * availablePx,
  );
  split.style.gridTemplateColumns =
    `${listPx}px ${gutterPx}px minmax(${MIN_DETAIL_REM}rem, 1fr)`;
}

function bindComplianceSplitResize(split: HTMLElement, gutter: HTMLElement): void {
  let listFraction = readStoredListFraction();
  let dragging = false;
  const mq = window.matchMedia(SPLIT_NARROW_MQ);

  const gutterPx = (): number => Math.max(gutter.getBoundingClientRect().width, 6);

  const applyStored = (): void => {
    if (mq.matches) {
      split.style.gridTemplateColumns = '';
      return;
    }
    const g = gutterPx();
    const available = split.getBoundingClientRect().width - g;
    const minList = remToPx(MIN_LIST_REM);
    const minDetail = remToPx(MIN_DETAIL_REM);
    // Panel is often bound before it is inserted into the document (width 0).
    // Wait for a real layout before applying; ResizeObserver will retry.
    if (available <= minList + minDetail) {
      return;
    }
    listFraction = clampListFraction(listFraction, available, minList, minDetail);
    applyComplianceSplitColumns(split, listFraction, available, g, minList, minDetail);
  };

  const applyFromPointer = (clientX: number): void => {
    if (mq.matches) return;
    const rect = split.getBoundingClientRect();
    const g = gutterPx();
    const available = rect.width - g;
    const minList = remToPx(MIN_LIST_REM);
    const minDetail = remToPx(MIN_DETAIL_REM);
    if (available <= minList + minDetail) return;
    const listPx = Math.min(Math.max(clientX - rect.left, minList), available - minDetail);
    listFraction = listPx / available;
    applyComplianceSplitColumns(split, listFraction, available, g, minList, minDetail);
  };

  applyStored();

  const resizeObserver = new ResizeObserver(() => {
    if (dragging) return;
    applyStored();
  });
  resizeObserver.observe(split);

  gutter.addEventListener('pointerdown', (event) => {
    if (mq.matches) return;
    event.preventDefault();
    gutter.setPointerCapture(event.pointerId);
    dragging = true;
    split.classList.add('sedr-compliance-split--dragging');
    applyFromPointer(event.clientX);
  });
  gutter.addEventListener('pointermove', (event) => {
    if (!gutter.hasPointerCapture(event.pointerId)) return;
    applyFromPointer(event.clientX);
  });
  const stopDrag = (event: PointerEvent) => {
    if (gutter.hasPointerCapture(event.pointerId)) {
      gutter.releasePointerCapture(event.pointerId);
    }
    dragging = false;
    split.classList.remove('sedr-compliance-split--dragging');
    if (!mq.matches) {
      writeStoredListFraction(listFraction);
    }
  };
  gutter.addEventListener('pointerup', stopDrag);
  gutter.addEventListener('pointercancel', stopDrag);

  const onMqChange = () => applyStored();
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', onMqChange);
  } else {
    // Safari < 14
    (mq as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(onMqChange);
  }
}

export interface SedrCompliancePanelState {
  statusFilter: SedrStatusFilter;
  categoryFilter: SedrCategoryFilter;
  phaseId: CompliancePhaseId;
  search: string;
  commentSearch: string;
  hideObsolete: boolean;
  /** Lifecycle subtypes represented by the Obsolete chip; defaults to all. */
  obsoleteSubtypes?: SedrStatusFilter;
  ownerFilter: SedrOwnerFilter;
  renewalFilter: SedrRenewalFilter;
  lifecycleRequestFilter: SedrLifecycleRequestFilter;
  evidenceFilter: SedrEvidenceFilter;
  selectedRequirementIds: string[];
}

export interface SedrCompliancePanelOptions {
  roomId: string;
  roomTitle: string;
  clientOrganization: string;
  requirements: SedrRequirement[];
  phases: SedrPhase[];
  files: SedrFile[];
  canWrite: boolean;
  canAdmin: boolean;
  showExport: boolean;
  showCreate: boolean;
  showImport: boolean;
  initial: SedrCompliancePanelState;
  onStateChange: (state: SedrCompliancePanelState) => void;
  onOpenFile: (file: SedrFile, context?: { gallery: SedrFile[] }) => void | Promise<void>;
  onDownloadFile?: (file: SedrFile) => void | Promise<void>;
  onImport?: () => void;
  onRequirementsChanged: () => Promise<void>;
  onOpenRequirement?: (requirementId: string) => void;
  onUploadFiles?: (
    parentId: string | null,
    files: File[],
    onProgress: (progress: BlobUploadProgress) => void,
  ) => Promise<SedrFilePickerUploadResult>;
  guest?: {
    roomToken?: string;
    guestInviteId?: string;
    accountRoomId?: string;
    canComment?: boolean;
    canApprove?: boolean;
  };
}

export interface SedrCompliancePanelUpdate {
  requirements: SedrRequirement[];
  phases: SedrPhase[];
  files: SedrFile[];
}

export interface SedrCompliancePanelController {
  element: HTMLElement;
  update(next: SedrCompliancePanelUpdate): void;
  refreshLocale(): void;
}

function appendTextCell(tr: HTMLTableRowElement, text: string): HTMLTableCellElement {
  const td = document.createElement('td');
  td.textContent = text;
  tr.append(td);
  return td;
}

function rowFingerprint(req: SedrRequirement): string {
  return [
    req.requirementCode,
    req.title,
    req.category,
    req.categoryName ?? '',
    req.phaseName ?? '',
    req.status,
    req.status === 'ApprovedDischarged' ? (req.approvedByDisplay ?? '') : '',
    req.ownerName ?? '',
    formatSedrRequirementLinks(req.links),
  ].join('\0');
}

function requirementDetailFingerprint(req: SedrRequirement): string {
  return [
    rowFingerprint(req),
    req.description ?? '',
    req.phaseId ?? '',
    req.linkedFileId ?? '',
    req.linkedFileName ?? '',
    req.canClientApprove ? '1' : '0',
    req.requestedLifecycleStatus ?? '',
    req.supersededByRequirementId ?? '',
    req.approvalExpiresAt ?? '',
    JSON.stringify(req.artifacts ?? []),
  ].join('\0');
}

function phasesFingerprint(phases: SedrPhase[]): string {
  return phases.map((p) => `${p.id}\0${p.name}\0${p.sortOrder}`).join('|');
}

function filesFingerprint(files: SedrFile[]): string {
  return files.map((f) => `${f.id}\0${f.name}\0${f.blobId ?? ''}\0${f.parentId ?? ''}`).join('|');
}

function paintRequirementRow(
  tr: HTMLTableRowElement,
  req: SedrRequirement,
  selected: boolean,
): void {
  tr.replaceChildren();
  tr.className = `sedr-req-row${selected ? ' sedr-req-row--selected' : ''}`;
  tr.dataset.reqId = req.id;
  tr.tabIndex = 0;
  tr.setAttribute('aria-selected', selected ? 'true' : 'false');
  tr.setAttribute('role', 'row');

  const codeTd = document.createElement('td');
  codeTd.className = 'sedr-req-row__code';
  const code = document.createElement('code');
  code.textContent = req.requirementCode;
  codeTd.append(code);
  const links = req.links ?? [];
  if (links.length > 0) {
    const hint = document.createElement('span');
    hint.className = 'sedr-req-row__links-hint muted';
    hint.title = formatSedrRequirementLinksTooltip(links);
    hint.setAttribute('aria-label', t('sedr.links.listHintCount', { count: links.length }));
    hint.append(iconImg('req-links', 12));
    codeTd.append(hint);
  }
  tr.append(codeTd);

  appendTextCell(tr, req.title);
  appendTextCell(tr, displaySedrCategory(req.category, req.categoryName, getCachedSedrCategories()));
  appendTextCell(tr, req.phaseName ?? '—');

  const statusTd = document.createElement('td');
  const badge = document.createElement('span');
  badge.className = statusBadgeClass(req.status);
  badge.textContent = tSedrStatus(req.status);
  statusTd.append(badge);
  tr.append(statusTd);

  appendTextCell(tr, req.status === 'ApprovedDischarged' ? (req.approvedByDisplay ?? '—') : '—');
  appendTextCell(tr, req.ownerName ?? '—');
}

export function renderSedrCompliancePanel(
  options: SedrCompliancePanelOptions,
): SedrCompliancePanelController {
  let statusFilter =
    options.initial.statusFilter.length > 0
      ? options.initial.statusFilter
      : options.initial.hideObsolete
        ? sedrAllActiveStatusFilter()
        : sedrAllStatusFilter();
  let categoryFilter = options.initial.categoryFilter;
  let phaseId = options.initial.phaseId;
  let search = options.initial.search;
  let commentSearch = options.initial.commentSearch ?? '';
  let commentMatchingIds: Set<string> | null = null;
  let commentSearchSeq = 0;
  let commentSearchTimer: number | null = null;
  let obsoleteSubtypes =
    (options.initial.obsoleteSubtypes?.length ?? 0) > 0
      ? options.initial.obsoleteSubtypes!.filter((s) =>
          (SEDR_LIFECYCLE_STATUSES as readonly string[]).includes(s),
        )
      : [...SEDR_LIFECYCLE_STATUSES];
  if (obsoleteSubtypes.length === 0) obsoleteSubtypes = [...SEDR_LIFECYCLE_STATUSES];
  const selectedObs = selectedObsoleteStatuses(statusFilter);
  if (selectedObs.length > 0) obsoleteSubtypes = selectedObs;
  let hideObsolete = statusFilterHidesObsolete(statusFilter);
  let ownerFilter = options.initial.ownerFilter ?? 'all';
  let renewalFilter = options.initial.renewalFilter ?? 'all';
  let lifecycleRequestFilter = options.initial.lifecycleRequestFilter ?? 'all';
  let evidenceFilter = options.initial.evidenceFilter ?? 'all';
  let filtersCollapsed = readFiltersCollapsed();
  let sort: SedrComplianceSort = readComplianceSort();
  let selectedRequirementIds = [...options.initial.selectedRequirementIds];
  /** Anchor for shift-click range (last non-shift selection). */
  let selectionAnchorId: string | null = selectedRequirementIds[0] ?? null;
  let mobileStep: SedrComplianceMobileStep =
    isSedrMobileLayout() && selectedRequirementIds.length > 0 ? 'detail' : 'list';
  let requirements = options.requirements;
  let phases = options.phases;
  let files = options.files;
  let searchTimer: number | null = null;
  let lastPhasesKey = phasesFingerprint(phases);
  let lastDetailKey = '';
  const rowFingerprintByEl = new WeakMap<HTMLTableRowElement, string>();

  const panel = document.createElement('div');
  panel.className = 'sedr-tab-panel sedr-tab-panel--compliance';
  panel.tabIndex = -1;

  const toolbarBits: string[] = [];
  if (options.showCreate && options.canWrite) {
    toolbarBits.push('<button type="button" class="button primary" id="sedr-create-req-btn" data-i18n="sedr.requirement.create"></button>');
  }
  if (options.showImport && options.canAdmin) {
    toolbarBits.push('<button type="button" class="button subtle" id="sedr-import-btn" data-i18n="sedr.import.button"></button>');
  }
  if (options.showExport) {
    toolbarBits.push('<button type="button" class="button subtle" id="sedr-export-btn" data-i18n="sedr.export.button"></button>');
  }

  const sortColumns: Array<{ key: SedrComplianceSortKey; labelKey: string }> = [
    { key: 'requirementCode', labelKey: 'sedr.table.reqId' },
    { key: 'title', labelKey: 'sedr.table.title' },
    { key: 'category', labelKey: 'sedr.table.category' },
    { key: 'phaseName', labelKey: 'sedr.phases.field' },
    { key: 'status', labelKey: 'sedr.table.status' },
    { key: 'approvedByDisplay', labelKey: 'sedr.table.approvedBy' },
    { key: 'ownerName', labelKey: 'sedr.table.owner' },
  ];

  const showMobileAdd =
    (options.showCreate && options.canWrite) || (options.showImport && options.canAdmin);

  panel.innerHTML = `
    ${toolbarBits.length > 0 ? `<div class="sedr-toolbar">${toolbarBits.join('')}</div>` : ''}
    <div class="sedr-compliance-mobile-bar" hidden>
      <button type="button" class="help-tip sedr-compliance-mobile-bar__btn" id="sedr-mobile-filters-btn" data-i18n-aria-label="sedr.filters.open"></button>
      <div class="sedr-compliance-mobile-bar__spacer"></div>
      <div class="sedr-compliance-mobile-bar__actions">
        ${showMobileAdd ? '<button type="button" class="help-tip sedr-compliance-mobile-bar__btn" id="sedr-mobile-add-btn" data-i18n-aria-label="sedr.compliance.mobileAdd"></button>' : ''}
        ${options.showExport ? '<button type="button" class="help-tip sedr-compliance-mobile-bar__btn" id="sedr-mobile-export-btn" data-i18n-aria-label="sedr.export.button"></button>' : ''}
      </div>
    </div>
    <div class="sedr-compliance-mobile-filters-done" hidden>
      <button type="button" class="button ghost" id="sedr-mobile-filters-done-btn" data-i18n="sedr.filters.done"></button>
    </div>
    <div class="sedr-compliance-split">
      <div class="sedr-compliance-list">
        <p class="sedr-compliance-count muted" id="sedr-compliance-count" aria-live="polite"></p>
        <div class="sedr-table-wrap">
          <table class="data-table sedr-compliance-table" role="grid">
            <thead>
              <tr id="sedr-compliance-thead-row"></tr>
            </thead>
            <tbody id="sedr-compliance-tbody"></tbody>
          </table>
        </div>
      </div>
      <button
        type="button"
        class="sedr-compliance-gutter"
        data-i18n-aria-label="sedr.compliance.resizeGutter"
      ></button>
      <aside class="sedr-requirement-panel-host" id="sedr-req-panel"></aside>
    </div>
  `;

  const mobileBar = panel.querySelector('.sedr-compliance-mobile-bar') as HTMLElement;
  const mobileFiltersDone = panel.querySelector('.sedr-compliance-mobile-filters-done') as HTMLElement;
  const mobileFiltersBtn = panel.querySelector('#sedr-mobile-filters-btn') as HTMLButtonElement | null;
  const mobileAddBtn = panel.querySelector('#sedr-mobile-add-btn') as HTMLButtonElement | null;
  const mobileExportBtn = panel.querySelector('#sedr-mobile-export-btn') as HTMLButtonElement | null;
  if (mobileFiltersBtn) {
    mobileFiltersBtn.append(iconImg('filter', 22));
  }
  if (mobileAddBtn) {
    mobileAddBtn.append(iconImg('plus', 22));
  }
  if (mobileExportBtn) {
    mobileExportBtn.append(iconImg('file-download', 22));
  }

  const emitState = () => {
    hideObsolete = statusFilterHidesObsolete(statusFilter);
    options.onStateChange({
      statusFilter,
      categoryFilter,
      phaseId,
      search,
      commentSearch,
      hideObsolete,
      obsoleteSubtypes: [...obsoleteSubtypes],
      ownerFilter,
      renewalFilter,
      lifecycleRequestFilter,
      evidenceFilter,
      selectedRequirementIds: [...selectedRequirementIds],
    });
  };

  const fetchCommentMatchingIds = async (query: string): Promise<string[]> => {
    const guest = options.guest;
    if (guest?.accountRoomId) {
      const res = await guestApi.searchRequirementsByComment(guest.accountRoomId, query);
      return res.requirementIds;
    }
    if (guest?.roomToken && guest.guestInviteId) {
      const res = await sedrApi.guestSearchRequirementsByComment(
        guest.roomToken,
        guest.guestInviteId,
        query,
      );
      return res.requirementIds;
    }
    const res = await sedrApi.searchRequirementsByComment(options.roomId, query);
    return res.requirementIds;
  };

  const scheduleCommentSearch = (value: string) => {
    if (commentSearchTimer != null) window.clearTimeout(commentSearchTimer);
    const trimmed = value.trim();
    if (!trimmed) {
      commentSearchSeq += 1;
      commentMatchingIds = null;
      paintTable();
      return;
    }
    commentSearchTimer = window.setTimeout(() => {
      commentSearchTimer = null;
      const seq = ++commentSearchSeq;
      const q = commentSearch.trim();
      if (!q) {
        commentMatchingIds = null;
        paintTable();
        return;
      }
      void fetchCommentMatchingIds(q)
        .then((ids) => {
          if (seq !== commentSearchSeq) return;
          commentMatchingIds = new Set(ids);
          paintTable();
        })
        .catch(() => {
          if (seq !== commentSearchSeq) return;
          commentMatchingIds = new Set();
          paintTable();
        });
    }, COMMENT_SEARCH_DEBOUNCE_MS);
  };

  const getFiltered = (): SedrRequirement[] => {
    let items = searchRequirements(
      filterRequirementsByPhase(
        applyRequirementFilters(
          requirements,
          statusFilter,
          categoryFilter,
          hideObsolete,
          ownerFilter,
          renewalFilter,
          evidenceFilter,
          lifecycleRequestFilter,
        ),
        phaseId,
      ),
      search,
    );
    if (commentSearch.trim()) {
      const ids = commentMatchingIds ?? new Set<string>();
      items = items.filter((r) => ids.has(r.id));
    }
    return sortRequirements(items, sort);
  };

  const countEl = panel.querySelector('#sedr-compliance-count') as HTMLParagraphElement;
  const theadRow = panel.querySelector('#sedr-compliance-thead-row') as HTMLTableRowElement;
  const tbody = panel.querySelector('#sedr-compliance-tbody') as HTMLTableSectionElement;
  const detailHost = panel.querySelector('#sedr-req-panel') as HTMLElement;
  const splitEl = panel.querySelector('.sedr-compliance-split') as HTMLElement;
  const gutterEl = panel.querySelector('.sedr-compliance-gutter') as HTMLElement;
  bindComplianceSplitResize(splitEl, gutterEl);

  const filtersAreActive = (): boolean => {
    if (search.trim()) return true;
    if (commentSearch.trim()) return true;
    if (categoryFilter !== 'all') return true;
    if (phaseId !== null) return true;
    if (ownerFilter !== 'all') return true;
    if (renewalFilter !== 'all') return true;
    if (lifecycleRequestFilter !== 'all') return true;
    if (evidenceFilter !== 'all') return true;
    const quick = detectSedrStatusQuickLink(statusFilter);
    const defaultQuick = hideObsolete ? 'allActive' : 'all';
    return quick !== defaultQuick;
  };

  const updateFilterActiveIndicator = () => {
    if (!mobileFiltersBtn) return;
    const active = filtersAreActive();
    mobileFiltersBtn.classList.toggle('sedr-compliance-mobile-bar__btn--active', active);
    mobileFiltersBtn.setAttribute('aria-pressed', active ? 'true' : 'false');
  };

  const syncMobileLayout = () => {
    const mobile = isSedrMobileLayout();
    panel.classList.toggle('sedr-tab-panel--compliance-mobile', mobile);
    for (const cls of Object.values(MOBILE_STEP_CLASSES)) {
      panel.classList.remove(cls);
    }

    if (!mobile) {
      mobileBar.hidden = true;
      mobileFiltersDone.hidden = true;
      return;
    }

    if (mobileStep === 'detail' && selectedRequirementIds.length === 0) {
      mobileStep = 'list';
    }

    panel.classList.add(MOBILE_STEP_CLASSES[mobileStep]);
    mobileBar.hidden = mobileStep !== 'list';
    mobileFiltersDone.hidden = mobileStep !== 'filters';
    updateFilterActiveIndicator();
  };

  const setMobileStep = (step: SedrComplianceMobileStep) => {
    mobileStep = step;
    syncMobileLayout();
  };

  const paintSortHeaders = () => {
    theadRow.replaceChildren();
    for (const col of sortColumns) {
      const th = document.createElement('th');
      th.scope = 'col';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sedr-compliance-sort';
      btn.dataset.sortKey = col.key;
      btn.setAttribute('data-i18n', col.labelKey);
      const active = sort.key === col.key;
      th.setAttribute('aria-sort', active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none');
      if (active) {
        btn.classList.add('sedr-compliance-sort--active');
        btn.dataset.sortDir = sort.dir;
      }
      btn.addEventListener('click', () => {
        if (sort.key === col.key) {
          sort = { key: col.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
        } else {
          sort = { key: col.key, dir: 'asc' };
        }
        writeComplianceSort(sort);
        paintSortHeaders();
        paintTable();
      });
      th.append(btn);
      theadRow.append(th);
    }
    applyI18n(theadRow);
  };

  const paintSelection = () => {
    const selected = new Set(selectedRequirementIds);
    for (const row of tbody.querySelectorAll('tr[data-req-id]')) {
      const id = row.getAttribute('data-req-id') ?? '';
      const isSelected = selected.has(id);
      row.classList.toggle('sedr-req-row--selected', isSelected);
      row.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    }
  };

  const computeDetailKey = (): string => {
    const ids = selectedRequirementIds.join(',');
    const reqsPart = selectedRequirementIds
      .map((id) => {
        const req = requirements.find((r) => r.id === id);
        return req ? requirementDetailFingerprint(req) : '';
      })
      .join('|');
    return `${ids}\n${reqsPart}\n${filesFingerprint(files)}\n${phasesFingerprint(phases)}`;
  };

  const clearSelectionToList = () => {
    selectedRequirementIds = [];
    selectionAnchorId = null;
    emitState();
    showSelectedPanel(true);
    if (isSedrMobileLayout()) {
      setMobileStep('list');
    }
  };

  const renderDetailPanel = () => {
    const selectedReqs = selectedRequirementIds
      .map((id) => requirements.find((r) => r.id === id))
      .filter((r): r is SedrRequirement => Boolean(r));

    if (selectedReqs.length >= 2) {
      void renderSedrBulkRequirementPanel(detailHost, {
        roomId: options.roomId,
        requirements: selectedReqs,
        phases,
        canWrite: options.canWrite,
        onClose: isSedrMobileLayout() ? clearSelectionToList : undefined,
        onUpdated: async () => {
          await options.onRequirementsChanged();
        },
        guest: options.guest
          ? {
              roomToken: options.guest.roomToken,
              guestInviteId: options.guest.guestInviteId,
              accountRoomId: options.guest.accountRoomId,
              canApprove: Boolean(options.guest.canApprove),
            }
          : undefined,
      });
      return;
    }

    const selected = selectedReqs[0] ?? null;
    void renderSedrRequirementPanel(detailHost, {
      roomId: options.roomId,
      requirement: selected,
      files,
      phases,
      requirements,
      canWrite: options.canWrite,
      onClose: isSedrMobileLayout() && selected ? clearSelectionToList : undefined,
      onOpenFile: (file, context) => options.onOpenFile(file, context),
      onDownloadFile: options.onDownloadFile
        ? (file) => options.onDownloadFile!(file)
        : undefined,
      onOpenRequirement: options.onOpenRequirement
        ? (requirementId) => options.onOpenRequirement!(requirementId)
        : undefined,
      onUploadFiles: options.onUploadFiles,
      onUpdated: async () => {
        await options.onRequirementsChanged();
      },
      guest: options.guest && selected
        ? {
            roomToken: options.guest.roomToken,
            guestInviteId: options.guest.guestInviteId,
            accountRoomId: options.guest.accountRoomId,
            canApprove: Boolean(options.guest.canApprove) && selected.canClientApprove,
            canComment: Boolean(options.guest.canComment),
            onApprove: async () => {
              if (options.guest!.accountRoomId) {
                await guestApi.approveRequirement(options.guest!.accountRoomId, selected.id);
              } else {
                await sedrApi.guestApproveRequirement(
                  options.guest!.roomToken!,
                  options.guest!.guestInviteId!,
                  selected.id,
                );
              }
              await options.onRequirementsChanged();
            },
            onDeny: async (reason: string) => {
              if (options.guest!.accountRoomId) {
                await guestApi.denyRequirement(options.guest!.accountRoomId, selected.id, reason);
              } else {
                await sedrApi.guestDenyRequirement(
                  options.guest!.roomToken!,
                  options.guest!.guestInviteId!,
                  selected.id,
                  reason,
                );
              }
              await options.onRequirementsChanged();
            },
          }
        : undefined,
    });
  };

  const showSelectedPanel = (force = false) => {
    paintSelection();
    const key = computeDetailKey();
    if (!force && key === lastDetailKey) {
      syncMobileLayout();
      return;
    }
    lastDetailKey = key;
    renderDetailPanel();
    syncMobileLayout();
  };

  const selectOnly = (id: string) => {
    selectedRequirementIds = [id];
    selectionAnchorId = id;
  };

  const toggleId = (id: string) => {
    if (selectedRequirementIds.includes(id)) {
      selectedRequirementIds = selectedRequirementIds.filter((x) => x !== id);
    } else {
      selectedRequirementIds = [...selectedRequirementIds, id];
    }
    selectionAnchorId = id;
  };

  const selectRange = (id: string, filtered: SedrRequirement[]) => {
    const anchor = selectionAnchorId && filtered.some((r) => r.id === selectionAnchorId)
      ? selectionAnchorId
      : (filtered[0]?.id ?? id);
    const start = filtered.findIndex((r) => r.id === anchor);
    const end = filtered.findIndex((r) => r.id === id);
    if (start < 0 || end < 0) {
      selectOnly(id);
      return;
    }
    const lo = Math.min(start, end);
    const hi = Math.max(start, end);
    selectedRequirementIds = filtered.slice(lo, hi + 1).map((r) => r.id);
  };

  const applySelection = (
    reqId: string,
    filtered: SedrRequirement[],
    event: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean },
  ) => {
    if (event.shiftKey) {
      selectRange(reqId, filtered);
    } else if (event.ctrlKey || event.metaKey) {
      toggleId(reqId);
    } else {
      selectOnly(reqId);
    }
    emitState();
    showSelectedPanel(true);
    if (isSedrMobileLayout()) {
      setMobileStep(selectedRequirementIds.length > 0 ? 'detail' : 'list');
    }
  };

  const paintEmptyRow = (total: number) => {
    for (const row of [...tbody.querySelectorAll('tr[data-req-id]')]) {
      row.remove();
    }
    let emptyRow = tbody.querySelector(`tr.${EMPTY_ROW_CLASS}`) as HTMLTableRowElement | null;
    if (!emptyRow) {
      emptyRow = document.createElement('tr');
      emptyRow.className = EMPTY_ROW_CLASS;
      const td = document.createElement('td');
      td.colSpan = 7;
      td.className = 'muted sedr-compliance-empty';
      emptyRow.append(td);
      tbody.append(emptyRow);
    }
    const td = emptyRow.querySelector('td') as HTMLTableCellElement;
    td.setAttribute(
      'data-i18n',
      total === 0 ? 'sedr.compliance.emptyRoom' : 'sedr.compliance.empty',
    );
    applyI18n(emptyRow);
  };

  const paintTable = (forceLocale = false) => {
    const filtered = getFiltered();
    const filteredIds = new Set(filtered.map((r) => r.id));
    const nextSelected = selectedRequirementIds.filter((id) => filteredIds.has(id));
    if (nextSelected.length !== selectedRequirementIds.length) {
      selectedRequirementIds = nextSelected;
      emitState();
    }
    if (selectionAnchorId && !filteredIds.has(selectionAnchorId)) {
      selectionAnchorId = selectedRequirementIds[0] ?? null;
    }

    const total = requirements.length;
    const shown = filtered.length;
    countEl.textContent = t('sedr.compliance.resultCount', { shown, total });

    if (filtered.length === 0) {
      paintEmptyRow(total);
      showSelectedPanel(forceLocale);
      return;
    }

    tbody.querySelector(`tr.${EMPTY_ROW_CLASS}`)?.remove();

    const existingById = new Map<string, HTMLTableRowElement>();
    for (const row of tbody.querySelectorAll<HTMLTableRowElement>('tr[data-req-id]')) {
      const id = row.dataset.reqId;
      if (id) existingById.set(id, row);
    }

    const selectedSet = new Set(selectedRequirementIds);
    const nextRows: HTMLTableRowElement[] = [];
    for (const req of filtered) {
      const fp = rowFingerprint(req);
      let tr = existingById.get(req.id);
      if (tr) {
        existingById.delete(req.id);
        if (forceLocale || rowFingerprintByEl.get(tr) !== fp) {
          paintRequirementRow(tr, req, selectedSet.has(req.id));
          rowFingerprintByEl.set(tr, fp);
        }
      } else {
        tr = document.createElement('tr');
        paintRequirementRow(tr, req, selectedSet.has(req.id));
        rowFingerprintByEl.set(tr, fp);
      }
      nextRows.push(tr);
    }

    for (const stale of existingById.values()) {
      stale.remove();
    }

    for (let i = 0; i < nextRows.length; i++) {
      const desired = nextRows[i]!;
      const current = tbody.children[i] as Element | undefined;
      if (current !== desired) {
        tbody.insertBefore(desired, current ?? null);
      }
    }

    applyI18n(tbody);
    const complianceTable = panel.querySelector('table.data-table') as HTMLTableElement | null;
    if (complianceTable) {
      enhanceDataTableCards(complianceTable);
    }
    showSelectedPanel(forceLocale);
  };

  const syncListChrome = () => {
    const mobile = isSedrMobileLayout();
    panel.classList.toggle('sedr-tab-panel--compliance-compact', !mobile && filtersCollapsed);
    const toolbar = panel.querySelector('.sedr-toolbar') as HTMLElement | null;
    if (toolbar) {
      toolbar.hidden = mobile || filtersCollapsed;
    }
    syncMobileLayout();
  };

  const mountFilterBar = () => {
    const mobile = isSedrMobileLayout();
    const filterBar = renderComplianceFilterBar({
      statusFilter,
      onStatusChange: (next) => {
        statusFilter = next;
        const selected = selectedObsoleteStatuses(next);
        if (selected.length > 0) obsoleteSubtypes = selected;
        hideObsolete = statusFilterHidesObsolete(next);
        emitState();
        mountFilterBar();
        paintTable();
        updateFilterActiveIndicator();
      },
      obsoleteSubtypes,
      onObsoleteSubtypesChange: (next) => {
        // Checked = shown. Remember last non-empty set for the main toggle-on.
        if (next.length > 0) obsoleteSubtypes = next;
        statusFilter = setObsoleteSubtypesInStatusFilter(statusFilter, next);
        hideObsolete = statusFilterHidesObsolete(statusFilter);
        emitState();
        paintTable();
        updateFilterActiveIndicator();
        const main = panel.querySelector(
          '.sedr-obsolete-split__main',
        ) as HTMLButtonElement | null;
        const split = panel.querySelector('.sedr-obsolete-split') as HTMLElement | null;
        const shown = selectedObsoleteStatuses(statusFilter);
        const active = shown.length > 0;
        if (main) {
          main.textContent = obsoleteFilterChipLabel(shown);
          main.classList.toggle('sedr-filter-chip--active', active);
          main.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
        split?.classList.toggle('sedr-obsolete-split--active', active);
        const quick = detectSedrStatusQuickLink(statusFilter);
        for (const btn of panel.querySelectorAll<HTMLButtonElement>('[data-quick]')) {
          const pressed = quick === btn.dataset.quick;
          btn.classList.toggle('sedr-filter-quick--active', pressed);
          btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        }
      },
      categoryFilter,
      categories: getCachedSedrCategories(),
      onCategoryChange: (next) => {
        categoryFilter = next;
        emitState();
        mountFilterBar();
        paintTable();
        updateFilterActiveIndicator();
      },
      phaseId,
      phases,
      onPhaseChange: (next) => {
        phaseId = next;
        emitState();
        mountFilterBar();
        paintTable();
        updateFilterActiveIndicator();
      },
      search,
      onSearchChange: (value) => {
        search = value;
        emitState();
        updateFilterActiveIndicator();
        if (searchTimer != null) window.clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => {
          searchTimer = null;
          paintTable();
        }, SEARCH_DEBOUNCE_MS);
      },
      commentSearch,
      onCommentSearchChange: (value) => {
        commentSearch = value;
        emitState();
        updateFilterActiveIndicator();
        // Clear matches immediately so we don't show stale IDs for a new query.
        if (value.trim()) commentMatchingIds = new Set();
        scheduleCommentSearch(value);
      },
      ownerFilter,
      owners: collectRequirementOwners(requirements),
      onOwnerChange: (next) => {
        ownerFilter = next;
        emitState();
        mountFilterBar();
        paintTable();
        updateFilterActiveIndicator();
      },
      renewalFilter,
      onRenewalChange: (next) => {
        renewalFilter = next;
        emitState();
        mountFilterBar();
        paintTable();
        updateFilterActiveIndicator();
      },
      lifecycleRequestFilter,
      onLifecycleRequestChange: (next) => {
        lifecycleRequestFilter = next;
        emitState();
        mountFilterBar();
        paintTable();
        updateFilterActiveIndicator();
      },
      evidenceFilter,
      onEvidenceChange: (next) => {
        evidenceFilter = next;
        emitState();
        mountFilterBar();
        paintTable();
        updateFilterActiveIndicator();
      },
      collapsed: mobile ? false : filtersCollapsed,
      hideCollapseToggle: mobile,
      onCollapsedChange: (next) => {
        filtersCollapsed = next;
        writeFiltersCollapsed(next);
        syncListChrome();
        mountFilterBar();
      },
    });
    const split = panel.querySelector('.sedr-compliance-split');
    if (split) {
      panel.querySelector('.sedr-compliance-filters')?.remove();
      panel.insertBefore(filterBar, split);
    }
    syncListChrome();
  };

  const refreshLocale = () => {
    if (!panel.isConnected) {
      unbindLocale();
      return;
    }
    applyI18n(panel);
    paintSortHeaders();
    mountFilterBar();
    paintTable(true);
  };

  const unbindLocale = bindLocaleRefresh(refreshLocale);

  tbody.addEventListener('mousedown', (event) => {
    const tr = (event.target as Element | null)?.closest?.('tr[data-req-id]');
    if (!tr || !tbody.contains(tr)) return;
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      event.preventDefault();
    }
  });

  tbody.addEventListener('click', (event) => {
    const tr = (event.target as Element | null)?.closest?.('tr[data-req-id]') as HTMLTableRowElement | null;
    if (!tr || !tbody.contains(tr)) return;
    const reqId = tr.dataset.reqId;
    if (!reqId) return;
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      event.preventDefault();
      window.getSelection()?.removeAllRanges();
    }
    applySelection(reqId, getFiltered(), event);
  });

  tbody.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const tr = (event.target as Element | null)?.closest?.('tr[data-req-id]') as HTMLTableRowElement | null;
    if (!tr || !tbody.contains(tr)) return;
    const reqId = tr.dataset.reqId;
    if (!reqId) return;
    event.preventDefault();
    applySelection(reqId, getFiltered(), { ctrlKey: false, metaKey: false, shiftKey: false });
  });

  paintSortHeaders();
  mountFilterBar();
  const guestCtx = options.guest
    ? {
        roomToken: options.guest.roomToken,
        guestInviteId: options.guest.guestInviteId,
        accountRoomId: options.guest.accountRoomId,
      }
    : undefined;
  void loadSedrCategories(options.roomId, false, guestCtx).then(() => {
    mountFilterBar();
    paintTable();
  });
  if (commentSearch.trim()) {
    scheduleCommentSearch(commentSearch);
  }

  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (isSedrMobileLayout() && mobileStep === 'filters') {
      event.preventDefault();
      setMobileStep('list');
      return;
    }
    if (selectedRequirementIds.length > 0) {
      event.preventDefault();
      clearSelectionToList();
    }
  });

  const openCreateDialog = () => {
    showSedrCreateRequirementDialog({
      roomId: options.roomId,
      phases,
      onCreated: () => options.onRequirementsChanged(),
    });
  };

  const openImport = () => {
    options.onImport?.();
  };

  const openExportDialog = () => {
    showSedrExportDialog({
      roomId: options.roomId,
      roomTitle: options.roomTitle,
      clientOrganization: options.clientOrganization,
      filteredRequirements: getFiltered(),
      allRequirements: requirements,
      allowServerPdf: Boolean(options.roomId) && !options.guest,
      hideObsolete,
    });
  };

  panel.querySelector('#sedr-create-req-btn')?.addEventListener('click', openCreateDialog);
  panel.querySelector('#sedr-import-btn')?.addEventListener('click', openImport);
  panel.querySelector('#sedr-export-btn')?.addEventListener('click', openExportDialog);

  mobileFiltersBtn?.addEventListener('click', () => setMobileStep('filters'));
  panel.querySelector('#sedr-mobile-filters-done-btn')?.addEventListener('click', () => {
    setMobileStep('list');
  });
  mobileExportBtn?.addEventListener('click', openExportDialog);
  mobileAddBtn?.addEventListener('click', () => {
    const items: FilesContextMenuItem[] = [];
    if (options.showCreate && options.canWrite) {
      items.push({
        id: 'create',
        labelKey: 'sedr.requirement.create',
        action: openCreateDialog,
      });
    }
    if (options.showImport && options.canAdmin) {
      items.push({
        id: 'import',
        labelKey: 'sedr.import.button',
        action: openImport,
      });
    }
    if (items.length === 0) return;
    openFilesContextMenu({
      anchorRect: mobileAddBtn.getBoundingClientRect(),
      items,
    });
  });

  const mobileMq = window.matchMedia(SEDR_MOBILE_LAYOUT_MQ);
  const onMobileMqChange = () => {
    if (!isSedrMobileLayout()) {
      mobileStep = 'list';
    } else if (selectedRequirementIds.length > 0) {
      mobileStep = 'detail';
    } else {
      mobileStep = 'list';
    }
    mountFilterBar();
    syncListChrome();
  };
  if (typeof mobileMq.addEventListener === 'function') {
    mobileMq.addEventListener('change', onMobileMqChange);
  } else {
    (mobileMq as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(onMobileMqChange);
  }

  applyI18n(panel);
  paintTable();
  syncMobileLayout();

  return {
    element: panel,
    update(next: SedrCompliancePanelUpdate) {
      const prevOwners = collectRequirementOwners(requirements).join('\0');
      requirements = next.requirements;
      files = next.files;
      const nextPhasesKey = phasesFingerprint(next.phases);
      phases = next.phases;
      const ownersChanged = collectRequirementOwners(requirements).join('\0') !== prevOwners;
      if (nextPhasesKey !== lastPhasesKey || ownersChanged) {
        lastPhasesKey = nextPhasesKey;
        mountFilterBar();
      }
      paintTable();
      syncMobileLayout();
    },
    refreshLocale,
  };
}
